import React, { createContext, useContext, ReactNode, useState, useEffect } from 'react';
import ISO6391, { LanguageCode } from 'iso-639-1';

const LANGUAGE_STORAGE_KEY = 'user_language';
const GEOIP_API = '/api/geoip?api=geoip2';

// Map ISO 3166-1 alpha-2 country codes → supported language codes
const COUNTRY_TO_LANGUAGE: Partial<Record<string, LanguageCode>> = {
  // Vietnamese
  vn: 'vi',
  // Chinese
  cn: 'zh', tw: 'zh', hk: 'zh', mo: 'zh', sg: 'zh',
  // Hindi (India)
  in: 'hi',
  // Spanish
  es: 'es', mx: 'es', ar: 'es', co: 'es', pe: 'es', ve: 'es', cl: 'es',
  ec: 'es', gt: 'es', cu: 'es', bo: 'es', do: 'es', hn: 'es', py: 'es',
  sv: 'es', ni: 'es', cr: 'es', pa: 'es', uy: 'es',
  // French
  fr: 'fr', be: 'fr', cd: 'fr', ci: 'fr', cm: 'fr', mg: 'fr', sn: 'fr',
  ml: 'fr', bf: 'fr', ne: 'fr', gn: 'fr', tg: 'fr', bj: 'fr',
  // Arabic
  sa: 'ar', eg: 'ar', iq: 'ar', sy: 'ar', jo: 'ar', lb: 'ar',
  ae: 'ar', kw: 'ar', qa: 'ar', bh: 'ar', om: 'ar', ye: 'ar',
  ly: 'ar', dz: 'ar', ma: 'ar', tn: 'ar', sd: 'ar',
  // Bengali
  bd: 'bn',
  // Portuguese
  br: 'pt', pt: 'pt', ao: 'pt', mz: 'pt', cv: 'pt', gw: 'pt',
  // Russian
  ru: 'ru', by: 'ru', kg: 'ru', kz: 'ru',
  // Urdu
  pk: 'ur',
  // Indonesian
  id: 'id',
  // German
  de: 'de', at: 'de', li: 'de',
  // Japanese
  jp: 'ja',
  // Swahili
  ke: 'sw', tz: 'sw', ug: 'sw',
  // Thai
  th: 'th',
};

export type Language = {
  code: LanguageCode;
  name: string;
  nativeName: string;
}

// Predefined list of supported languages
const SUPPORTED_LANGUAGES: Language[] = [
  { "name": "English", "nativeName": "English", "code": "en" },
  { "name": "Mandarin Chinese", "nativeName": "中文 (Zhōngwén) / 普通话 (Pǔtōnghuà)", "code": "zh" },
  { "name": "Hindi", "nativeName": "हिन्दी (Hindī)", "code": "hi" },
  { "name": "Spanish", "nativeName": "Español", "code": "es" },
  { "name": "French", "nativeName": "Français", "code": "fr" },
  { "name": "Modern Standard Arabic", "nativeName": "العربية (al-ʿArabīyah)", "code": "ar" },
  { "name": "Bengali", "nativeName": "বাংলা (Bangla)", "code": "bn" },
  { "name": "Portuguese", "nativeName": "Português", "code": "pt" },
  { "name": "Russian", "nativeName": "Русский (Russkiy)", "code": "ru" },
  { "name": "Urdu", "nativeName": "اُردُو (Urdū)", "code": "ur" },
  { "name": "Indonesian", "nativeName": "Bahasa Indonesia", "code": "id" },
  { "name": "German", "nativeName": "Deutsch", "code": "de" },
  { "name": "Japanese", "nativeName": "日本語 (Nihongo)", "code": "ja" },
  { "name": "Swahili", "nativeName": "Kiswahili", "code": "sw" },
  { "name": "Marathi", "nativeName": "मराठी (Marāṭhī)", "code": "mr" },
  { "name": "Vietnamese", "nativeName": "Tiếng Việt", "code": "vi" },
  { "name": "Thai", "nativeName": "ไทย (Thai)", "code": "th" }
];

interface LanguageContextType {
  language: LanguageCode;
  updateLanguage: (language: LanguageCode) => void;
  getSupportedLanguages: () => Language[];
  getCurrentLanguageInfo: () => Language;
  getLanguageName: (languageCode: LanguageCode) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguage] = useState<LanguageCode>('en');

  // Load language from localStorage on mount; fall back to GeoIP detection for first-time visitors
  useEffect(() => {
    const savedLanguage = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (savedLanguage && ISO6391.validate(savedLanguage)) {
      setLanguage(savedLanguage as LanguageCode);
      return;
    }

    console.log('[LanguageContext] No saved language, fetching GeoIP...');
    fetch(GEOIP_API)
      .then(res => res.json())
      .then(data => {
        console.log('[LanguageContext] GeoIP response:', data);
        const countryCode: string | undefined = data?.data?.country?.iso_code?.toLowerCase();
        if (!countryCode) {
          console.warn('[LanguageContext] GeoIP: no country code in response');
          return;
        }
        const detectedLang = COUNTRY_TO_LANGUAGE[countryCode];
        console.log(`[LanguageContext] country=${countryCode} → lang=${detectedLang ?? 'not mapped, keep en'}`);
        if (detectedLang && SUPPORTED_LANGUAGES.some(l => l.code === detectedLang)) {
          setLanguage(detectedLang);
          localStorage.setItem(LANGUAGE_STORAGE_KEY, detectedLang);
        }
      })
      .catch(err => { console.error('[LanguageContext] GeoIP fetch error:', err); });
  }, []);

  // Update language and save to localStorage
  const updateLanguage = (newLanguage: LanguageCode) => {
    setLanguage(newLanguage);
    localStorage.setItem(LANGUAGE_STORAGE_KEY, newLanguage);
  };

  // Get supported languages from predefined list
  const getSupportedLanguages = (): Language[] => {
    return SUPPORTED_LANGUAGES.sort((a, b) => a.nativeName.localeCompare(b.nativeName)); // Sort alphabetically by native name
  };

  // Get current language info
  const getCurrentLanguageInfo = (): Language => {
    const currentLang = SUPPORTED_LANGUAGES.find(lang => lang.code === language);
    if (currentLang) {
      return currentLang;
    }
    // Fallback to ISO6391 if language not in supported list
    return {
      code: language,
      name: ISO6391.getName(language),
      nativeName: ISO6391.getNativeName(language)
    } as Language;
  };

  // Get language name from language code
  const getLanguageName = (languageCode: LanguageCode): string => {
    const lang = SUPPORTED_LANGUAGES.find(l => l.code === languageCode);
    if (lang) {
      return lang.nativeName;
    }
    // Fallback to ISO6391 if language not in supported list
    return ISO6391.getNativeName(languageCode);
  };

  const contextValue: LanguageContextType = {
    language,
    updateLanguage,
    getSupportedLanguages,
    getCurrentLanguageInfo,
    getLanguageName,
  };

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
